import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useApp }  from "../context/AppContext";

const STEPS    = ["Service Type","Personal Info","Travel Details","Review & Submit"];
const SERVICES_ALL = [
  {v:"Air Ticket",        icon:"✈️",   d:"Domestic & International flights"},
  {v:"Hotel",             icon:"🏨",   d:"Budget to 5-star properties"},
  {v:"Visa",              icon:"🛂",   d:"Tourist, Business, Student"},
  {v:"Air + Hotel",       icon:"✈️🏨", d:"Flight & accommodation combo"},
  {v:"Air + Hotel + Visa",icon:"🌐",   d:"Complete bundle"},
  {v:"Hotel + Visa",      icon:"🏨🛂", d:"Accommodation with visa"},
];
// Only "Visa" is offered for now — change this back to SERVICES_ALL to bring the other service
// types (Air Ticket, Hotel, combos) back once they're ready.
const SERVICES = SERVICES_ALL.filter(sv => sv.v === "Visa");

const INIT = {
  service_type:"Visa",
  full_name:"",dob:"",gender:"",nationality:"",
  passport_no:"",passport_expiry:"",email:"",phone:"",address:"",
  passport_photo:"",passport_photo_name:"",
  passport_copy:"",passport_copy_name:"",
  destination:"",travel_purpose:"",travel_date:"",return_date:"",
  airline_pref:"Any Airline",hotel_category:"Not Required",
  visa_type:"Tourist",special_req:""
};
const MAX_PHOTO_BYTES = 25 * 1024;   // 25 KB — applicant photograph
const MAX_COPY_BYTES  = 600 * 1024;  // 600 KB — passport page scan (PDF only)
const TODAY_STR = new Date().toISOString().split("T")[0]; // yyyy-mm-dd, for date-picker min/max limits

export default function ApplicantForm({ navigate }) {
  const { user, logout } = useAuth();
  const { addApp }       = useApp();
  const [step,   setStep]   = useState(0);
  const [errors, setErrors] = useState({});
  const [form,   setForm]   = useState(INIT);

  const upd = k => e => {
    setForm(p => ({...p, [k]: e.target.value}));
    setErrors(p => ({...p, [k]: ""}));
  };

  // phone: allow only digits, spaces and a leading + (blocks letters/symbols while typing)
  const updPhone = e => {
    const cleaned = e.target.value.replace(/[^\d+\s]/g, "");
    setForm(p => ({...p, phone: cleaned}));
    setErrors(p => ({...p, phone: ""}));
  };

  const makeFileHandler = (field, nameField, maxBytes, allowPdf=false, pdfOnly=false) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const okType = pdfOnly
      ? file.type === "application/pdf"
      : (file.type.startsWith("image/") || (allowPdf && file.type==="application/pdf"));
    if (!okType) {
      setErrors(p => ({...p, [field]: pdfOnly ? "Please upload a valid PDF file." : (allowPdf ? "Please upload a valid image or PDF file." : "Please upload a valid image file (JPG/PNG).")}));
      e.target.value = "";
      return;
    }
    if (file.size > maxBytes) {
      setErrors(p => ({...p, [field]: `File too large (${(file.size/1024).toFixed(1)} KB). Max allowed size is ${(maxBytes/1024).toFixed(0)} KB.`}));
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm(p => ({...p, [field]: reader.result, [nameField]: file.name}));
      setErrors(p => ({...p, [field]: ""}));
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const handlePhoto        = makeFileHandler("passport_photo","passport_photo_name", MAX_PHOTO_BYTES, false);
  const handlePassportCopy = makeFileHandler("passport_copy", "passport_copy_name",  MAX_COPY_BYTES,  false, true);
  const removePhoto        = () => setForm(p => ({...p, passport_photo:"", passport_photo_name:""}));
  const removePassportCopy = () => setForm(p => ({...p, passport_copy:"",  passport_copy_name:""}));

  /* ── VALIDATION ── */
  const validate = (n) => {
    const e = {};
    if (n === 1) {
      if (!form.full_name.trim() || form.full_name.trim().length < 2)
        e.full_name = "Full name is required";
      if (!form.dob)
        e.dob = "Date of birth is required";
      else if (form.dob > TODAY_STR)
        e.dob = "Date of birth cannot be in the future";
      if (!form.gender)
        e.gender = "Please select gender";
      if (!form.nationality)
        e.nationality = "Please select nationality";
      if (!form.passport_no.trim() || form.passport_no.trim().length < 5)
        e.passport_no = "Enter valid passport number (min 5 chars)";
      if (!form.passport_expiry)
        e.passport_expiry = "Passport expiry date is required";
      else if (form.passport_expiry < TODAY_STR)
        e.passport_expiry = "Passport is already expired — a valid passport is required";
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
        e.email = "Enter a valid email address";
      if (!form.phone.trim() || form.phone.replace(/\D/g,"").length < 10)
        e.phone = "Phone number must be at least 10 digits";
      if (!form.passport_photo)
        e.passport_photo = "Applicant photograph is required";
      if (!form.passport_copy)
        e.passport_copy = "Passport copy is required";
    }
    if (n === 2) {
      if (!form.destination.trim() || form.destination.trim().length < 2)
        e.destination = "Destination is required";
      if (!form.travel_purpose)
        e.travel_purpose = "Please select travel purpose";
      if (!form.travel_date)
        e.travel_date = "Travel date is required";
      else if (form.travel_date < TODAY_STR)
        e.travel_date = "Travel date cannot be in the past";
      if (!form.return_date)
        e.return_date = "Return date is required";
      if (form.travel_date && form.return_date && form.return_date <= form.travel_date)
        e.return_date = "Return date must be after travel date";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validate(step)) { setStep(s => s+1); window.scrollTo(0,0); }
  };
  const back = () => { setStep(s => s-1); setErrors({}); };

  const submit = async () => {
    const id = await addApp({
      ...form,
      agent_name:  user.name,
      agent_agency:user.agency,
      agent_email: user.email,
    });
    navigate("success", { app_id: id });
  };

  /* ── STYLE HELPERS ── */
  const IS = (k) => ({
    width:"100%", padding:"12px 14px",
    border: `1.5px solid ${errors[k] ? "#e05252" : "#dde3ed"}`,
    borderRadius:10, fontSize:13,
    fontFamily:"'DM Sans',sans-serif",
    color:"#0d1f3c",
    background: errors[k] ? "#fff8f8" : "#fafbfd",
    outline:"none", boxSizing:"border-box", transition:"all 0.2s"
  });
  const SS = (k) => ({...IS(k), cursor:"pointer"});
  const lbl = (text, req) => (
    <label style={{fontSize:11,fontWeight:600,letterSpacing:"1.5px",textTransform:"uppercase",color:"#6b7a90",marginBottom:6,display:"block"}}>
      {text}{req && <span style={{color:"#c9a227"}}> *</span>}
    </label>
  );
  const Err = ({k}) => errors[k] ? <span style={{fontSize:11,color:"#e05252",marginTop:3,display:"block"}}>{errors[k]}</span> : null;

  /* ── SUB COMPONENTS ── */
  const CardHdr = ({icon,title,sub}) => (
    <div style={{padding:"20px 26px",borderBottom:"1px solid #eaeef5",display:"flex",alignItems:"center",gap:14,background:"linear-gradient(135deg,#fafbfd,#fff)"}}>
      <div style={{width:44,height:44,borderRadius:12,background:"linear-gradient(135deg,#0d1f3c,#162844)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{icon}</div>
      <div>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:21,fontWeight:600,color:"#0d1f3c",margin:0}}>{title}</h2>
        {sub && <p style={{fontSize:12,color:"#6b7a90",marginTop:2}}>{sub}</p>}
      </div>
    </div>
  );

  const ErrBanner = () => Object.keys(errors).length > 0 ? (
    <div style={{background:"#fff0f0",border:"1.5px solid #e05252",borderRadius:10,padding:"13px 16px",marginBottom:18,fontSize:13,color:"#c0392b"}}>
      <strong style={{display:"block",marginBottom:5}}>⚠ Please fill in the required fields:</strong>
      <ul style={{paddingLeft:18,margin:0,lineHeight:1.9}}>{Object.values(errors).map((e,i)=><li key={i}>{e}</li>)}</ul>
    </div>
  ) : null;

  const NavBtns = ({showBack=true,onNext,nextLabel,isSubmit=false}) => (
    <div style={{display:"flex",justifyContent:showBack?"space-between":"flex-end",padding:"20px 26px",borderTop:"1px solid #eaeef5",background:"#fafbfd"}}>
      {showBack && (
        <button onClick={back} style={{padding:"12px 24px",background:"#eaeef5",color:"#1a2540",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>← Back</button>
      )}
      <button onClick={isSubmit ? submit : onNext}
        style={{padding:"12px 26px",background:isSubmit?"linear-gradient(135deg,#c9a227,#e8b84b)":"linear-gradient(135deg,#0d1f3c,#162844)",
        color:isSubmit?"#0d1f3c":"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
        {nextLabel}
      </button>
    </div>
  );

  const ReviewItem = ({label,value}) => (
    <div style={{background:"#f6f8fc",borderRadius:10,padding:"11px 14px"}}>
      <div style={{fontSize:10,letterSpacing:"1.5px",textTransform:"uppercase",color:"#8a9bb5",fontWeight:600,marginBottom:3}}>{label}</div>
      <div style={{fontSize:13,color:"#1a2540",fontWeight:500,wordBreak:"break-word"}}>{value || "—"}</div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"#f0f3f9",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`
        select option{color:#0d1f3c;}
        textarea:focus,input:focus,select:focus{border-color:#4a7fb5 !important;background:#fff !important;box-shadow:0 0 0 3px rgba(74,127,181,0.1) !important;}
        @media (max-width:768px){
          .af-nav{padding:0 16px !important;}
          .af-agency-badge{display:none !important;}
          .af-hero{padding:20px 16px 16px !important;}
          .af-hero h1{font-size:22px !important;}
          .af-tabs-wrap{padding:0 16px !important;}
          .af-tab-sub{display:none !important;}
          .af-tab-label{font-size:11px !important;}
          .af-tab-item{padding:12px 6px !important;gap:6px !important;}
          .af-form-wrap{padding:0 16px 60px !important;}
        }
      `}</style>

      {/* NAV */}
      <nav className="af-nav" style={{background:"#0d1f3c",padding:"0 36px",height:62,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,borderBottom:"2px solid #4a7fb5"}}>
        <img src="/logo-small.png" alt="AIHOVI" style={{height:54,objectFit:"contain",filter:"drop-shadow(0 1px 6px rgba(0,0,0,0.3))"}} onError={e=>e.target.style.display="none"}/>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div className="af-agency-badge" style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(74,127,181,0.3)",borderRadius:24,padding:"5px 14px",fontSize:12,color:"#fff",display:"flex",alignItems:"center",gap:7}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:"#4CAF50",display:"inline-block",flexShrink:0}}/>
            {user?.name} · {user?.agency}
          </div>
          <button onClick={()=>navigate("agent-dashboard")} style={{background:"transparent",border:"1px solid rgba(74,127,181,0.4)",color:"#8ec1ff",padding:"6px 15px",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>📋 My Applications</button>
          <button onClick={logout} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.2)",color:"#fff",padding:"6px 15px",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Sign Out</button>
        </div>
      </nav>

      {/* HERO */}
      <div className="af-hero" style={{background:"linear-gradient(135deg,#0d1f3c,#162844)",padding:"28px 36px 22px",borderBottom:"1px solid rgba(74,127,181,0.15)"}}>
        <div style={{maxWidth:980,margin:"0 auto"}}>
          <div style={{fontSize:11,color:"#8a9bb5",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:7}}>Dashboard → New Application</div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:32,fontWeight:700,color:"#f8f5ef",marginBottom:3}}>New Travel Application</h1>
          <p style={{fontSize:13,color:"#8a9bb5"}}>Complete all sections to submit your client's travel request</p>
        </div>
      </div>

      {/* STEP TABS */}
      <div className="af-tabs-wrap" style={{background:"#fff",borderBottom:"1px solid #e4e9f0",padding:"0 36px"}}>
        <div style={{maxWidth:980,margin:"0 auto",display:"flex"}}>
          {STEPS.map((s,i) => (
            <div key={i} className="af-tab-item" style={{flex:1,padding:"15px 16px",display:"flex",alignItems:"center",gap:10,borderBottom:`3px solid ${step===i?"#4a7fb5":"transparent"}`,opacity:step===i?1:i<step?0.75:0.4,transition:"all 0.2s"}}>
              <div style={{width:26,height:26,borderRadius:"50%",background:step===i?"#4a7fb5":i<step?"#0d1f3c":"#eaeef5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:600,color:step===i||i<step?"#fff":"#6b7a90",flexShrink:0,transition:"all 0.2s"}}>
                {i < step ? "✓" : i+1}
              </div>
              <div>
                <div className="af-tab-label" style={{fontSize:12,fontWeight:500,color:"#1a2540"}}>{s}</div>
                <div className="af-tab-sub" style={{fontSize:10,color:"#8a9bb5"}}>{["Air/Hotel/Visa","Applicant Details","Itinerary & Prefs","Confirm & Send"][i]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FORM CARD */}
      <div className="af-form-wrap" style={{maxWidth:980,margin:"26px auto",padding:"0 36px 80px"}}>
        <div style={{background:"#fff",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 16px rgba(10,22,40,0.07)",border:"1px solid #e4e9f0"}}>

          {/* ── STEP 0: Service Type ── */}
          {step === 0 && (<>
            <CardHdr icon="🎯" title="Select Service Type" sub="Choose what your client needs"/>
            <div style={{padding:"26px 26px 20px"}}>
              <div className="rgrid-3" style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(SERVICES.length,3)},1fr)`,gap:14,maxWidth:SERVICES.length===1?260:"none",margin:SERVICES.length===1?"0 auto":0}}>
                {SERVICES.map(sv => (
                  <div key={sv.v} onClick={()=>setForm(p=>({...p,service_type:sv.v}))}
                    style={{display:"flex",flexDirection:"column",alignItems:"center",gap:9,padding:"20px 14px",border:`2px solid ${form.service_type===sv.v?"#4a7fb5":"#dde3ed"}`,borderRadius:13,cursor:"pointer",background:form.service_type===sv.v?"linear-gradient(135deg,#eef4ff,#f8fbff)":"#fafbfd",boxShadow:form.service_type===sv.v?"0 0 0 3px rgba(74,127,181,0.12)":"none",transition:"all 0.2s"}}>
                    <span style={{fontSize:30}}>{sv.icon}</span>
                    <span style={{fontSize:12,fontWeight:600,color:"#1a2540",textAlign:"center"}}>{sv.v}</span>
                    <span style={{fontSize:10,color:"#6b7a90",textAlign:"center",lineHeight:1.4}}>{sv.d}</span>
                  </div>
                ))}
              </div>
            </div>
            <NavBtns showBack={false} onNext={next} nextLabel="Personal Info →"/>
          </>)}

          {/* ── STEP 1: Personal Info ── */}
          {step === 1 && (<>
            <CardHdr icon="👤" title="Applicant Personal Information" sub={<>Fields marked <span style={{color:"#c9a227"}}>*</span> are required</>}/>
            <div style={{padding:"24px 26px"}}>
              <ErrBanner/>
              <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>

                <div>{lbl("Full Name (as per passport)",true)}
                  <input type="text" value={form.full_name} onChange={upd("full_name")} placeholder="Enter full name exactly as printed on passport" style={IS("full_name")}/>
                  <Err k="full_name"/>
                </div>

                <div>{lbl("Date of Birth",true)}
                  <input type="date" value={form.dob} onChange={upd("dob")} max={TODAY_STR} style={IS("dob")}/>
                  <Err k="dob"/>
                </div>

                <div>{lbl("Gender",true)}
                  <select value={form.gender} onChange={upd("gender")} style={SS("gender")}>
                    <option value="">-- Select Gender --</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <Err k="gender"/>
                </div>

                <div>{lbl("Nationality",true)}
                  <select value={form.nationality} onChange={upd("nationality")} style={SS("nationality")}>
                    <option value="">-- Select Nationality --</option>
                    <option value="Indian">Indian</option>
                    <option value="Afghan">Afghan</option>
                    <option value="American">American</option>
                    <option value="Australian">Australian</option>
                    <option value="Bangladeshi">Bangladeshi</option>
                    <option value="British">British</option>
                    <option value="Canadian">Canadian</option>
                    <option value="Chinese">Chinese</option>
                    <option value="Dubai / UAE">Dubai / UAE</option>
                    <option value="French">French</option>
                    <option value="German">German</option>
                    <option value="Nepali">Nepali</option>
                    <option value="Pakistani">Pakistani</option>
                    <option value="Sri Lankan">Sri Lankan</option>
                    <option value="Other">Other</option>
                  </select>
                  <Err k="nationality"/>
                </div>

                <div>{lbl("Passport Number",true)}
                  <input type="text" value={form.passport_no} onChange={upd("passport_no")} placeholder="e.g. A1234567" maxLength={20} style={IS("passport_no")}/>
                  <Err k="passport_no"/>
                </div>

                <div>{lbl("Passport Expiry Date",true)}
                  <input type="date" value={form.passport_expiry} onChange={upd("passport_expiry")} min={TODAY_STR} style={IS("passport_expiry")}/>
                  <Err k="passport_expiry"/>
                </div>

                <div style={{gridColumn:"1/-1"}}>
                  {lbl("Applicant Photograph (Max 25 KB)",true)}
                  <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                    {form.passport_photo ? (
                      <div style={{display:"flex",alignItems:"center",gap:10,background:"#f6f8fc",border:"1px solid #dde3ed",borderRadius:10,padding:"8px 12px"}}>
                        <img src={form.passport_photo} alt="Applicant" style={{width:52,height:52,objectFit:"cover",borderRadius:8,border:"1px solid #dde3ed"}}/>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:"#1a2540"}}>{form.passport_photo_name}</div>
                          <div style={{fontSize:11,color:"#16a34a"}}>✓ Uploaded</div>
                        </div>
                        <button type="button" onClick={removePhoto} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,background:"#fee2e2",color:"#dc2626",border:"none",cursor:"pointer"}}>Remove</button>
                      </div>
                    ) : (
                      <label style={{display:"inline-flex",alignItems:"center",gap:8,padding:"11px 18px",background:"#f6f8fc",border:"1.5px dashed #b8c4d8",borderRadius:10,fontSize:12,fontWeight:600,color:"#4a7fb5",cursor:"pointer"}}>
                        📷 Choose Photo
                        <input type="file" accept="image/*" onChange={handlePhoto} style={{display:"none"}}/>
                      </label>
                    )}
                  </div>
                  <div style={{fontSize:11,color:"#8a9bb5",marginTop:6}}>Passport-size photograph of the applicant. JPG/PNG only, under 25 KB.</div>
                  <Err k="passport_photo"/>
                </div>

                <div style={{gridColumn:"1/-1"}}>
                  {lbl("Passport Copy (Max 600 KB)",true)}
                  <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                    {form.passport_copy ? (
                      <div style={{display:"flex",alignItems:"center",gap:10,background:"#f6f8fc",border:"1px solid #dde3ed",borderRadius:10,padding:"8px 12px"}}>
                        {form.passport_copy.startsWith("data:image") ? (
                          <img src={form.passport_copy} alt="Passport Copy" style={{width:52,height:52,objectFit:"cover",borderRadius:8,border:"1px solid #dde3ed"}}/>
                        ) : (
                          <div style={{width:52,height:52,borderRadius:8,background:"#eaeef5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>📄</div>
                        )}
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:"#1a2540"}}>{form.passport_copy_name}</div>
                          <div style={{fontSize:11,color:"#16a34a"}}>✓ Uploaded</div>
                        </div>
                        <button type="button" onClick={removePassportCopy} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,background:"#fee2e2",color:"#dc2626",border:"none",cursor:"pointer"}}>Remove</button>
                      </div>
                    ) : (
                      <label style={{display:"inline-flex",alignItems:"center",gap:8,padding:"11px 18px",background:"#f6f8fc",border:"1.5px dashed #b8c4d8",borderRadius:10,fontSize:12,fontWeight:600,color:"#4a7fb5",cursor:"pointer"}}>
                        🪪 Choose File
                        <input type="file" accept="application/pdf" onChange={handlePassportCopy} style={{display:"none"}}/>
                      </label>
                    )}
                  </div>
                  <div style={{fontSize:11,color:"#8a9bb5",marginTop:6}}>Scan of the passport bio-data page. PDF only, under 600 KB.</div>
                  <Err k="passport_copy"/>
                </div>

                <div>{lbl("Email Address",true)}
                  <input type="email" value={form.email} onChange={upd("email")} placeholder="applicant@email.com" style={IS("email")}/>
                  <Err k="email"/>
                </div>

                <div>{lbl("Phone Number",true)}
                  <input type="tel" inputMode="numeric" value={form.phone} onChange={updPhone} placeholder="+91 98765 43210" style={IS("phone")}/>
                  <Err k="phone"/>
                </div>

                <div style={{gridColumn:"1/-1"}}>
                  {lbl("Residential Address")}
                  <textarea value={form.address} onChange={upd("address")} placeholder="House No, Street, City, State, PIN Code" rows={3}
                    style={{...IS("address"),resize:"vertical",minHeight:80,lineHeight:1.5}}/>
                </div>

              </div>
            </div>
            <NavBtns onNext={next} nextLabel="Travel Details →"/>
          </>)}

          {/* ── STEP 2: Travel Details ── */}
          {step === 2 && (<>
            <CardHdr icon="🗺️" title="Travel & Itinerary Details" sub={<>Fields marked <span style={{color:"#c9a227"}}>*</span> are required</>}/>
            <div style={{padding:"24px 26px"}}>
              <ErrBanner/>
              <div className="rgrid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>

                <div>{lbl("Destination Country / City",true)}
                  <select value={form.destination} onChange={upd("destination")} style={SS("destination")}>
                    <option value="">-- Select Destination --</option>
                    <option>UMRAH VISA UNDER 15 DAYS</option>
                    <option>UMRAH VISA UNDER 20 DAYS </option>
                    <option>SAUDI TRANSIT VISA </option>
                    <option>Dubai 30 Days</option>
                    <option>DUBAI 60 DAYS  NORMAL </option>
                    <option>DUBAI 60 DAYS URGENT</option>
                    <option>DUBAI 60 DAYS TOP URGENT </option>
                    <option>RUSSIA 30 DAYS</option>
                    <option>OMAN 10 DAYS  </option>
                    <option>OMAN 30 DAYS</option>     
                  </select>
                  <Err k="destination"/>
                </div>

                <div>{lbl("Purpose of Travel",true)}
                  <select value={form.travel_purpose} onChange={upd("travel_purpose")} style={SS("travel_purpose")}>
                    <option value="">-- Select Purpose --</option>
                    <option>Tourist</option>
                    <option>E Visa</option>
                    <option>Transit Visa</option>
                    <option>Pilgrimage/religious</option>
                    <option>Others</option>
                  </select>
                  <Err k="travel_purpose"/>
                </div>

                <div>{lbl("Departure / Travel Date",true)}
                  <input type="date" value={form.travel_date} onChange={upd("travel_date")} min={TODAY_STR} style={IS("travel_date")}/>
                  <Err k="travel_date"/>
                </div>

                <div>{lbl("Return Date",true)}
                  <input type="date" value={form.return_date} onChange={upd("return_date")} min={form.travel_date || TODAY_STR} style={IS("return_date")}/>
                  <Err k="return_date"/>
                </div>

                <div>{lbl("Preferred Airline")}
                  <select value={form.airline_pref} onChange={upd("airline_pref")} style={SS("airline_pref")}>
                    <option>Any Airline</option>
                    <option>Air India</option>
                    <option>IndiGo</option>
                    <option>SpiceJet</option>
                    <option>Vistara</option>
                    <option>Go First</option>
                    <option>Emirates</option>
                    <option>Etihad Airways</option>
                    <option>Qatar Airways</option>
                    <option>Singapore Airlines</option>
                    <option>British Airways</option>
                    <option>Lufthansa</option>
                    <option>Air Arabia</option>
                    <option>FlyDubai</option>
                    <option>Thai Airways</option>
                    <option>Oman Air</option>
                  </select>
                </div>

                <div>{lbl("Hotel Category")}
                  <select value={form.hotel_category} onChange={upd("hotel_category")} style={SS("hotel_category")}>
                    <option>Not Required</option>
                    <option>2 Star / Budget</option>
                    <option>3 Star</option>
                    <option>4 Star</option>
                    <option>5 Star</option>
                    <option>5 Star Deluxe / Luxury</option>
                  </select>
                </div>

                <div>{lbl("Visa Type Required")}
                  <select value={form.visa_type} onChange={upd("visa_type")} style={SS("visa_type")}>
                    <option>Tourist</option>
                    <option>Transit</option>
                    <option>E visa</option>
                    <option>Other</option>
                  </select>
                </div>

                <div style={{gridColumn:"1/-1"}}>
                  {lbl("Special Requirements / Notes")}
                  <textarea value={form.special_req} onChange={upd("special_req")}
                    placeholder="e.g. Wheelchair access, vegetarian meals, specific hotel area, visa urgency, travel insurance needed..." rows={3}
                    style={{...IS("special_req"),resize:"vertical",minHeight:80,lineHeight:1.5}}/>
                </div>

              </div>
            </div>
            <NavBtns onNext={next} nextLabel="Review →"/>
          </>)}

          {/* ── STEP 3: Review & Submit ── */}
          {step === 3 && (<>
            <CardHdr icon="✅" title="Review & Submit" sub="Please verify all details before submitting to AIHOVI"/>
            <div style={{padding:"24px 26px"}}>

              {/* Service badge */}
              <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"linear-gradient(135deg,#0d1f3c,#162844)",color:"#fff",padding:"8px 18px",borderRadius:24,fontSize:13,fontWeight:600,marginBottom:20}}>
                {SERVICES.find(s=>s.v===form.service_type)?.icon} {form.service_type}
              </div>

              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,color:"#0d1f3c",marginBottom:13,paddingBottom:8,borderBottom:"1px solid #f0f3f9"}}>Personal Information</h3>
              {(form.passport_photo || form.passport_copy) && (
                <div style={{display:"flex",gap:20,marginBottom:14,flexWrap:"wrap"}}>
                  {form.passport_photo && (
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <img src={form.passport_photo} alt="Applicant" style={{width:64,height:64,objectFit:"cover",borderRadius:10,border:"1px solid #dde3ed"}}/>
                      <div style={{fontSize:12,color:"#6b7a90"}}>Applicant Photograph<br/><strong style={{color:"#1a2540"}}>{form.passport_photo_name}</strong></div>
                    </div>
                  )}
                  {form.passport_copy && (
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      {form.passport_copy.startsWith("data:image") ? (
                        <img src={form.passport_copy} alt="Passport Copy" style={{width:64,height:64,objectFit:"cover",borderRadius:10,border:"1px solid #dde3ed"}}/>
                      ) : (
                        <div style={{width:64,height:64,borderRadius:10,background:"#f6f8fc",border:"1px solid #dde3ed",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>📄</div>
                      )}
                      <div style={{fontSize:12,color:"#6b7a90"}}>Passport Copy<br/><strong style={{color:"#1a2540"}}>{form.passport_copy_name}</strong></div>
                    </div>
                  )}
                </div>
              )}
              <div className="rgrid-3" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:22}}>
                <ReviewItem label="Full Name"        value={form.full_name}/>
                <ReviewItem label="Date of Birth"    value={form.dob}/>
                <ReviewItem label="Gender"           value={form.gender}/>
                <ReviewItem label="Nationality"      value={form.nationality}/>
                <ReviewItem label="Passport Number"  value={form.passport_no}/>
                <ReviewItem label="Passport Expiry"  value={form.passport_expiry}/>
                <ReviewItem label="Email Address"    value={form.email}/>
                <ReviewItem label="Phone Number"     value={form.phone}/>
                <ReviewItem label="Address"          value={form.address}/>
              </div>

              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,color:"#0d1f3c",marginBottom:13,paddingBottom:8,borderBottom:"1px solid #f0f3f9"}}>Travel Details</h3>
              <div className="rgrid-3" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:22}}>
                <ReviewItem label="Destination"      value={form.destination}/>
                <ReviewItem label="Travel Purpose"   value={form.travel_purpose}/>
                <ReviewItem label="Travel Date"      value={form.travel_date}/>
                <ReviewItem label="Return Date"      value={form.return_date}/>
                <ReviewItem label="Preferred Airline"value={form.airline_pref}/>
                <ReviewItem label="Hotel Category"   value={form.hotel_category}/>
                <ReviewItem label="Visa Type"        value={form.visa_type}/>
                <div style={{gridColumn:"1/-1"}}><ReviewItem label="Special Requirements" value={form.special_req||"None"}/></div>
              </div>

              <div style={{padding:"14px 18px",background:"#fffef0",borderRadius:10,border:"1px solid #e8d88a",fontSize:12,color:"#7a6b2a",lineHeight:1.7}}>
                ⚠ By submitting this form, you confirm that all information provided is accurate and complete. You confirm you are an authorized AIHOVI B2B agent. This application will be reviewed by AIHOVI admin within 24 working hours.
              </div>
            </div>
            <NavBtns isSubmit={true} nextLabel="🚀 Submit Application"/>
          </>)}

        </div>
      </div>
    </div>
  );
}
